<template>
  <div    v-if="show"
          class="bg-white overflow-hidden shadow rounded-lg h-full">
    <div class="px-4 py-5 sm:p-6 h-full">
      <div class="flex justify-between items-center mb-2">
        <!-- Point Name & Last Update Value -->
        <a      :href="company.buildCompanyRoute('meters/' + id)"
                class="flex items-center plain-link">
          <div :class="'text-base text-center text-white rounded-md p-3 mr-3 ' + getStatusColor">
            <div :class="'lnr ' + getStatusIcon">
            </div>
          </div>
          <div class="flex flex-col items-left">
            <span class="font-normal text-base text-gray-500">{{ data.Name }}</span><br/>
            <span class="font-normal text-base ml-1">{{ lastUpdateValue }} {{ data.unit }}</span>
          </div>
        </a>

        <div class="flex items-center justify-end">
          <!-- Alarm Icon & Link -->
          <a      v-if="hasActiveAlarm || hasSnoozedAlarm"
                  class="flex plain-link text-white cursor-pointer"
                  :href="company.buildCompanyRoute('alarms/' + data.alarms[0].AlarmID)">
            <div :class="'shrink-0 mr-3 p-3 rounded-md ' + (hasActiveAlarm ? 'bg-red-500' : 'bg-yellow-300')">

              <div   v-if="hasActiveAlarm"
                     class="lnr icon-alarm-ringing text-white"></div>
              <div   v-else-if="hasSnoozedAlarm"
                     class="lnr icon-alarm-snooze text-white"></div>
            </div>
          </a>

          <meter-favorite :favorite="isFavorite"
                          :meter-id="id"
          ></meter-favorite>
        </div>
      </div>

      <div class="flex flex-col justify-center items-center w-full h-32" :id="chartId">

        <div v-if="loading"
             class="flex flex-row justify-around my-16">
          <div class="flex align-center">
            <span class="lnr icon-loading animate-spin ml-3"></span>
            <span class="mt-1 ml-2">
              Loading Data ... Please Wait.
            </span>
          </div>
        </div>

        <div v-else-if="loading && hasChartData && data.data.length > 0"
             class="w-full">
          <span class="text-center text-base text-gray-500">
            Creating Graph ... Please Wait
          </span>
        </div>

        <div v-else-if="!loading && !hasChartData"
             class="text-center text-base text-gray-500 my-16">
          <p>
            No Data Found in the last 24 Hours.
          </p>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {amchartsMixin} from "@/mixins/amchartsMixin";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";

import axios from "axios";
import MeterFavorite from "@/components/meterFavorite";

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

export default {
  name: "DashboardPoint",
  mixins: [SonicuCoreMixin, amchartsMixin],
  components: [MeterFavorite],
  props: {
    id: '',
    isFavorite: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      data: {},
      loading: true,
      chart: null,
      chartData: [],
      show: true,
    }
  },
  created() {
    this.initializeAM5Charts();
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.loading = true;
      axios.get(this.company.buildCompanyApiInternalRoute('dashboard/point/' + this.id)).then(
          response => {
            this.data = response.data[0]
            this.loading = false
            if (this.data.data.length > 0) {
              this.buildChartData()
              this.makeChart()
            }
          }
      ).catch(() => {
          console.error('Error loading data for dashboard point', this.id);
          this.loading = false
      })
    },
    makeChart() {
      let root = am5.Root.new(this.chartId);

      let chart = root.container.children.push(
          am5xy.XYChart.new(root, {
            panY: false,
            layout: root.verticalLayout,
            paddingTop: 10,
            paddingBottom: 10
          })
      );

      let yAxis = chart.yAxes.push(
          am5xy.ValueAxis.new(root, {
            strictMinMax: true, // This will make sure that the chart is bound by the min and max values. It looks better this way.
            renderer: am5xy.AxisRendererY.new(root, {}),
            visible: false // Hide the labels
          })
      );
      yAxis.get('renderer').grid.template.set('forceHidden', true); // This hides the grid lines

      let xAxis = chart.xAxes.push(
          am5xy.DateAxis.new(root, {
            baseInterval:{ count: 1, timeUnit: 'second' }, // When doing a date axis, you need to set the base interval
            renderer: am5xy.AxisRendererX.new(root, {}),
            visible: false // Hide the labels
          })
      );
     xAxis.get('renderer').grid.template.set('forceHidden', true); // This hides the grid lines

      let createSeries = (name, field, settings) => {
        let series = chart.series.push(
            am5xy.LineSeries.new(root, {
              name: name,
              xAxis: xAxis,
              yAxis: yAxis,
              valueYField: field,
              valueXField: "date",
              stroke: settings.stroke,
            })
        );
        series.strokes.template.setAll({
          ...settings
        });
        series.data.setAll(this.chartData);
      }

      if (this.chartData[0].high_caution === null || this.chartData[0].caution_time === null) {
        createSeries('High_alarm', 'high_alarm', {
          stroke: am5.color('#9b1b1b'),
          strokeDasharray: [15, 15],
          tensionX: 1,
          strokeWidth: 1
        });
      }
      else {
        createSeries('High_caution', 'high_caution', {
          stroke: am5.color('#9b1b1b'),
          strokeDasharray: [15, 15],
          tensionX: 1,
          strokeWidth: 1
        });
      }

      if (this.chartData[0].low_caution === null || this.chartData[0].caution_time === null) {
        createSeries('Low_alarm', 'low_alarm', {
          stroke: am5.color('#1e429e'),
          strokeDasharray: [15, 15],
          tensionX: 1,
          strokeWidth: 1
        });
      }
      else {
        createSeries('Low_caution', 'low_caution', {
          stroke: am5.color('#1e429e'),
          strokeDasharray: [15, 15],
          tensionX: 1,
          strokeWidth: 1
        });
      }

      createSeries('Value', 'value', {
        stroke: am5.color('#3d81f7'),
        tensionX: 0.8,
        strokeWidth: 2
      });

      let labelContainer = chart.children.unshift(am5.Container.new(root, {
        width: am5.percent(100),
        paddingLeft: 0,
        paddingRight: 0
      }));

      let date = new Date(this.chartData[0].date)
      // This is the label that will be displayed on the left side of the chart
      labelContainer.children.unshift(am5.Label.new(root, {
        text: `${WEEKDAYS[date.getDay()]}, ${date.getHours()}:${(date.getMinutes() < 10 ? '0' : '') + date.getMinutes()}`,
        fontSize: 10,
        textAlign: "left",
        x: am5.percent(0),
        paddingLeft: 0,
        paddingBottom: 10
      }));

      // This is the label that will be displayed on the right side of the chart
      labelContainer.children.unshift(am5.Label.new(root, {
        text: "Most Recent",
        fontSize: 10,
        textAlign: "right",
        x: am5.percent(100),
        paddingBottom: 10,
        paddingRight: 0
      }));

      this.chart = chart
      return chart
    },
    buildChartData() {
      let chartData = []
      Object.keys(this.data.data).forEach(key => {
        let value = this.data.data[key]
        let time = this.data.data[key][this.timeKey]

        let timestamp
        if (typeof time === 'number') {
          // Unix timestamp - convert to milliseconds
          timestamp = time * 1000
        } else {
          // ISO string with timezone - parse and convert to milliseconds
          timestamp = new Date(time).getTime()
        }

        chartData.push({
          date: timestamp, // AM5 needs timestamp in milliseconds
          value: parseFloat(value[this.valueKey].toFixed(1)), // Parsing this as float allows us to use AM5's dynamic scaling
          low_alarm: this.data.alarm_profile.low_alarm_value,
          high_alarm: this.data.alarm_profile.high_alarm_value,
          low_caution: this.data.alarm_profile.low_caution_value,
          high_caution: this.data.alarm_profile.high_caution_value,
          caution_time: this.data.alarm_profile.high_caution_time,
        })
      })
      this.chartData = chartData
      return chartData
    },
  },
  computed: {
    lastUpdateValue() {
      if (!this.data.data || this.data.data.length === 0) {
        return '-'
      }
      return this.data.data[this.data.data.length - 1][this.valueKey].toFixed(1)
    },
    hasChartData() {
      return !this.loading && this.chartData.length !== 0
    },
    chartId() {
      return 'chartMeter-' + this.id
    },
    valueKey() {
      if (!this.data.data || this.data.data.length === 0) {
        return ''
      }

      let point = this.data.data[0]
      if ('mA' in point) {
        return 'mA'
      }
      else if ('dBLevel' in point) {
        return 'dBLevel'
      }
      else if ('data' in point) {
        return 'data'
      }
      else {
        return ''
      }
    },
    timeKey() {
      if (!this.data.data || this.data.data.length === 0) {
        return ''
      }

      let point = this.data.data[0]
      if ('Time' in point) {
        return 'Time'
      }
      else if ('time' in point) {
        return 'time'
      }
      else {
        return 'Stamp'
      }
    },
    hasActiveAlarm() {
      // If there are any alarms that are not snoozed then there are active alarms and we should return true
      return !(this.hasSnoozedAlarm || !this.data.alarms || this.data.alarms.length === 0);
    },
    hasSnoozedAlarm() {
      if (!this.data.alarms || this.data.alarms.length === 0) {
        return false
      }
      let snoozedAlarm = false
      this.data.alarms.forEach(alarm => {
        if (alarm.snooze_until !== null) {
          snoozedAlarm = true
        }
      })

      return snoozedAlarm
    },
    getStatusColor() {
      if (this.data.Level === 1) {
        return 'bg-green-500'
      }
      else if (this.data.Level === 2) {
        return 'bg-yellow-300'
      }
      else if (this.data.Level === 3) {
        return 'bg-red-500'
      }
      else {
        return 'bg-gray-500'
      }
    },
    getStatusIcon() {
      if (this.data.Level === 1) {
        return 'icon-check'
      }
      else if (this.data.Level === 2) {
        return 'icon-warning'
      }
      else if (this.data.Level === 3) {
        return 'icon-cross-circle'
      }
      else {
        return 'icon-cloud-crossed'
      }
    },
  },
  beforeUnmount() {
    this.chart.dispose()
  },
}
</script>

<style scoped>

</style>